/**
 * Token metadata resolution for screens where people approve transfers
 * (ENTX-A-2 / H-14).
 *
 * A proposal's `tokenSymbol` / `tokenDecimals` are metadata stored next to the
 * signed object, not part of it. They used to come straight from the proposer;
 * the relay now resolves them server-side, but this app still treats them as
 * untrusted input:
 *
 *  - Decimals must be an integer in [0, 36]. Anything else would either crash
 *    the page (`10n ** BigInt(-1)` throws) or scale the amount wrongly, so it
 *    is replaced by "raw base units" and the proposal is flagged.
 *  - When the contract (EVM) or mint (Solana) is on the curated list, the
 *    curated symbol and decimals are displayed. Metadata whose decimals
 *    contradict the curated list is flagged as a mismatch (fail closed).
 *  - Symbols are display text only: control, bidi and zero-width characters
 *    are stripped and the length is clamped, so a right-to-left override in "USDC" or a 200-char
 *    label can't spoof the action card.
 */

import { findKnownToken } from '@/lib/knownTokens';
import { chainDecimals, chainSymbols } from '@/types/vault';

/** Largest decimals value any supported chain uses (and the relay accepts). */
export const MAX_TOKEN_DECIMALS = 36;
const MAX_SYMBOL_LENGTH = 16;

/** Canonical Solana stablecoin mints (mirrors the relay's usdValuationService). */
const CURATED_SOLANA_MINTS: Record<string, { symbol: string; decimals: number }> = {
  EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: { symbol: 'USDC', decimals: 6 },
  Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: { symbol: 'USDT', decimals: 6 },
};

/** True for an integer number of decimals in [0, MAX_TOKEN_DECIMALS]. */
export function isValidTokenDecimals(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= MAX_TOKEN_DECIMALS
  );
}

// C0/C1 controls, zero-width and bidi override/isolate characters.
const UNSAFE_SYMBOL_CHARS =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2069\ufeff]/g;

/** Display-safe token symbol, or null when nothing printable is left. */
export function sanitizeTokenSymbol(symbol: unknown): string | null {
  if (typeof symbol !== 'string') return null;
  const cleaned = symbol.replace(UNSAFE_SYMBOL_CHARS, '').trim();
  if (!cleaned) return null;
  return cleaned.length > MAX_SYMBOL_LENGTH ? `${cleaned.slice(0, MAX_SYMBOL_LENGTH)}…` : cleaned;
}

/** Curated symbol/decimals for a contract (EVM) or mint (Solana), if any. */
export function findCuratedToken(
  chain: string,
  contract: string
): { symbol: string; decimals: number } | undefined {
  if (chain === 'solMainnet') return CURATED_SOLANA_MINTS[contract];
  const known = findKnownToken(chain, contract);
  return known ? { symbol: known.symbol, decimals: known.decimals } : undefined;
}

export type AssetMetaSource =
  /** Native coin of the chain — decimals from the chain table. */
  | 'native'
  /** Contract/mint on the curated list — curated symbol + decimals shown. */
  | 'curated'
  /** Not curated; server-resolved metadata passed validation. */
  | 'server'
  /** Not curated and the metadata is missing/invalid — raw base units shown. */
  | 'invalid';

export interface ResolvedAssetMeta {
  symbol: string;
  /** null = the amount can't be scaled safely; show raw base units. */
  decimals: number | null;
  source: AssetMetaSource;
  /** Proposal decimals contradict the curated list (blocking). */
  decimalsMismatch: boolean;
  /** Proposal symbol differs from the curated symbol (informational). */
  symbolMismatch: boolean;
  /** The proposal's own decimals value was present but invalid. */
  invalidDecimals: boolean;
}

export interface AssetRef {
  chain: string;
  tokenContract?: string;
  tokenSymbol?: unknown;
  tokenDecimals?: unknown;
}

/** Resolve what to display for a proposal/transaction asset. Never throws. */
export function resolveAssetMeta(asset: AssetRef): ResolvedAssetMeta {
  const invalidDecimals =
    asset.tokenDecimals !== undefined &&
    asset.tokenDecimals !== null &&
    !isValidTokenDecimals(asset.tokenDecimals);

  if (!asset.tokenContract) {
    const decimals = chainDecimals[asset.chain];
    return {
      symbol: chainSymbols[asset.chain] ?? asset.chain.toUpperCase(),
      decimals: decimals ?? null,
      source: 'native',
      decimalsMismatch: false,
      symbolMismatch: false,
      invalidDecimals: false,
    };
  }

  const claimedSymbol = sanitizeTokenSymbol(asset.tokenSymbol);
  const curated = findCuratedToken(asset.chain, asset.tokenContract);
  if (curated) {
    return {
      symbol: curated.symbol,
      decimals: curated.decimals,
      source: 'curated',
      decimalsMismatch:
        asset.tokenDecimals !== undefined &&
        asset.tokenDecimals !== null &&
        asset.tokenDecimals !== curated.decimals,
      symbolMismatch:
        claimedSymbol !== null && claimedSymbol.toUpperCase() !== curated.symbol.toUpperCase(),
      invalidDecimals,
    };
  }

  const validDecimals = isValidTokenDecimals(asset.tokenDecimals) ? asset.tokenDecimals : null;
  return {
    symbol: claimedSymbol ?? 'Unknown token',
    decimals: validDecimals,
    source: validDecimals === null ? 'invalid' : 'server',
    decimalsMismatch: false,
    symbolMismatch: false,
    invalidDecimals,
  };
}

/** True when the metadata is unsafe to approve against (fail closed). */
export function assetMetaBlocksApproval(meta: ResolvedAssetMeta): boolean {
  return meta.decimalsMismatch || meta.invalidDecimals || meta.source === 'invalid';
}

/** Parse a base-unit amount string; null when it is not a non-negative integer. */
export function parseBaseUnits(raw: unknown): bigint | null {
  if (typeof raw !== 'string' || !/^\d{1,78}$/.test(raw)) return null;
  return BigInt(raw);
}

/**
 * Format a base-unit amount with validated decimals. Returns the raw string
 * unchanged when it can't be scaled (invalid decimals or a non-integer amount)
 * — callers label that as base units. Never throws.
 */
export function formatUnitsSafe(raw: string, decimals: number | null): string {
  const value = parseBaseUnits(raw);
  if (value === null || decimals === null || !isValidTokenDecimals(decimals)) return raw;
  if (decimals === 0) return value.toString();
  const divisor = 10n ** BigInt(decimals);
  const whole = value / divisor;
  const remainder = value % divisor;
  if (remainder === 0n) return whole.toString();
  const frac = remainder.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole.toString()}.${frac}`;
}

/** Sum recipient amounts, ignoring malformed entries. Never throws. */
export function sumBaseUnits(amounts: readonly unknown[]): string {
  let total = 0n;
  for (const amount of amounts) {
    const value = parseBaseUnits(amount);
    if (value !== null) total += value;
  }
  return total.toString();
}

export interface FormattedAssetAmount {
  /** Scaled amount, or the raw base-unit string when it can't be scaled. */
  amount: string;
  /** Display-safe symbol (curated when the contract/mint is curated). */
  symbol: string;
  /** True when `amount` is unscaled base units (metadata invalid). */
  baseUnits: boolean;
  meta: ResolvedAssetMeta;
}

/** One-stop formatting for a proposal/transfer amount. Never throws. */
export function formatAssetAmount(raw: string, asset: AssetRef): FormattedAssetAmount {
  const meta = resolveAssetMeta(asset);
  return {
    amount: formatUnitsSafe(raw, meta.decimals),
    symbol: meta.symbol,
    baseUnits: meta.decimals === null,
    meta,
  };
}
